import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarPlus, Copy, RefreshCw, Trash2, Link2, CheckCircle2, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";

export default function CalendarSyncPanel() {
    const [open, setOpen] = useState(false);
    const [sync, setSync] = useState(null);
    const [newCal, setNewCal] = useState({ name: "", url: "" });
    const [adding, setAdding] = useState(false);
    const [busyCal, setBusyCal] = useState(null);

    useEffect(() => {
        api.get("/crm/calendar-sync").then((r) => setSync(r.data)).catch((e) => console.error(e));
        const current = new URL(window.location.href);
        const result = current.searchParams.get("google_calendar");
        if (result === "connected") toast.success("Google Calendar collegato");
        if (result === "error") toast.error("Collegamento Google Calendar non riuscito");
        if (result) {
            current.searchParams.delete("google_calendar");
            window.history.replaceState({}, "", `${current.pathname}${current.search}${current.hash}`);
        }
    }, []);

    const reload = async () => {
        const r = await api.get("/crm/calendar-sync");
        setSync(r.data);
    };

    const copyFeed = () => {
        navigator.clipboard.writeText(sync.feed_url);
        toast.success("Link copiato! Incollalo in Google Calendar o Outlook");
    };
    const addCal = async () => {
        if (!newCal.url.trim()) { toast.error("Inserisci l'URL ICS del tuo calendario"); return; }
        setAdding(true);
        try {
            await api.post("/crm/calendar-sync/external", newCal);
            toast.success("Calendario collegato e sincronizzato");
            setNewCal({ name: "", url: "" });
            const r = await api.get("/crm/calendar-sync");
            setSync(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore collegamento calendario"); }
        finally { setAdding(false); }
    };
    const syncNow = async (c) => {
        setBusyCal(c.id);
        try {
            await api.post(`/crm/calendar-sync/external/${c.id}/sync`);
            const r = await api.get("/crm/calendar-sync");
            setSync(r.data);
            toast.success("Sincronizzato");
        } catch (e) { toast.error("Errore sincronizzazione"); }
        finally { setBusyCal(null); }
    };
    const removeCal = async (c) => {
        if (!window.confirm(`Scollegare il calendario "${c.name}"?`)) return;
        await api.delete(`/crm/calendar-sync/external/${c.id}`);
        const r = await api.get("/crm/calendar-sync");
        setSync(r.data);
    };
    const connectGoogle = async () => {
        try {
            const { data } = await api.post("/crm/calendar-sync/google/connect");
            if (!data.authorization_url) {
                toast.error(data.detail || "Google Calendar non configurato");
                return;
            }
            window.location.assign(data.authorization_url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Collegamento Google Calendar non riuscito");
        }
    };
    const disconnectGoogle = async () => {
        if (!window.confirm("Scollegare Google Calendar? Gli eventi già creati su Google non verranno eliminati.")) return;
        await api.delete("/crm/calendar-sync/google");
        await reload();
        toast.success("Google Calendar scollegato");
    };

    return (
        <div data-testid="calendar-sync-section" className="feature-card-neu p-5">
            <button
                type="button"
                data-testid="calendar-sync-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between text-ink-400 text-[11px] uppercase tracking-wider"
            >
                <span className="flex items-center gap-2"><CalendarPlus size={15} /> Sincronizzazione calendari</span>
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {open && (
                <div className="mt-4">
                    <p className="text-xs text-ink-400 mb-4">Collega Google Calendar o Outlook: i tuoi impegni esterni appaiono nel calendario VendiDirect e l'agenda VendiDirect appare nel tuo calendario personale.</p>

                    <div data-testid="google-calendar-sync-card" className="mb-5 border border-white/10 bg-white/[.025] p-3.5">
                        <div className="flex flex-wrap items-center gap-3">
                            {sync?.google?.connected ? <CheckCircle2 size={15} className="text-green-400" /> : <CalendarPlus size={15} className="text-ink-400" />}
                            <div className="min-w-0 flex-1">
                                <div className="text-xs text-ink-100 font-medium">Google Calendar bidirezionale</div>
                                <p data-testid="google-calendar-sync-status" className="text-[11px] text-ink-500 mt-1">
                                    {sync?.google?.connected ? "Collegato · nuovi eventi e modifiche vengono sincronizzati" : "Non collegato · gli eventi restano disponibili solo in VendiDirect"}
                                </p>
                            </div>
                            {sync?.google?.connected ? (
                                <button data-testid="google-calendar-disconnect" type="button" onClick={disconnectGoogle} className="cta-outline px-3 py-2 text-xs">Scollega</button>
                            ) : (
                                <button data-testid="google-calendar-connect" type="button" onClick={connectGoogle} disabled={sync?.google?.configured === false} className="cta-gold px-3 py-2 text-xs disabled:opacity-40">Collega Google</button>
                            )}
                        </div>
                        {!!sync?.google?.pending_syncs && <p data-testid="google-calendar-pending-syncs" className="mt-2 text-[11px] text-amber-300">{sync.google.pending_syncs} operazioni Google in attesa di recupero.</p>}
                        {sync?.google?.last_error && <p data-testid="google-calendar-last-error" className="mt-1 text-[11px] text-red-300">Ultimo errore Google registrato. Gli eventi locali sono salvi.</p>}
                    </div>

                    <div className="mb-5">
                        <div className="text-xs text-ink-100 mb-2 font-medium">1 · Vedi l'agenda VendiDirect nel tuo calendario</div>
                        <div className="flex gap-2 items-center">
                            <input data-testid="ics-feed-url" readOnly value={sync?.feed_url || ""} className="flex-1 bg-black/30 border border-white/15 px-3 py-2 text-xs text-ink-400 outline-none font-mono truncate" />
                            <button data-testid="ics-feed-copy" onClick={copyFeed} className="cta-outline px-3 py-2 text-xs flex items-center gap-1.5 shrink-0"><Copy size={12} /> Copia link</button>
                        </div>
                        <p className="text-[11px] text-ink-500 mt-2">Google Calendar: Impostazioni → Aggiungi calendario → Da URL. Outlook: Aggiungi calendario → Sottoscrivi dal Web. Visite e impegni si aggiornano automaticamente.</p>
                    </div>

                    <div>
                        <div className="text-xs text-ink-100 mb-2 font-medium">2 · Porta il tuo calendario dentro VendiDirect</div>
                        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                            <input data-testid="ext-cal-name" value={newCal.name} onChange={(e) => setNewCal({ ...newCal, name: e.target.value })} placeholder="Nome (es. Google di Luca)" className="sm:col-span-2 bg-transparent border border-white/15 px-3 py-2 text-xs text-ink-50 focus:border-gold outline-none" />
                            <input data-testid="ext-cal-url" value={newCal.url} onChange={(e) => setNewCal({ ...newCal, url: e.target.value })} placeholder="URL ICS privato (https://...)" className="sm:col-span-2 bg-transparent border border-white/15 px-3 py-2 text-xs text-ink-50 focus:border-gold outline-none font-mono" />
                            <button data-testid="ext-cal-add" onClick={addCal} disabled={adding} className="cta-gold px-3 py-2 text-xs flex items-center justify-center gap-1.5"><Link2 size={12} /> {adding ? "Collego..." : "Collega"}</button>
                        </div>
                        <p className="text-[11px] text-ink-500 mt-2">Google Calendar: Impostazioni del calendario → "Indirizzo segreto in formato iCal". Outlook: Impostazioni → Calendari condivisi → Pubblica → link ICS. Sincronizzazione automatica ogni 15 minuti.</p>

                        {(sync?.externals || []).length > 0 && (
                            <div className="mt-3 border border-white/10 divide-y divide-white/5">
                                {sync.externals.map((c) => (
                                    <div key={c.id} data-testid={`ext-cal-row-${c.id}`} className="p-3 flex items-center gap-3 text-xs">
                                        {c.error ? <AlertCircle size={13} className="text-red-400 shrink-0" /> : <CheckCircle2 size={13} className="text-green-400 shrink-0" />}
                                        <span className="text-ink-50 truncate">{c.name}</span>
                                        <span className="text-ink-500 truncate flex-1">
                                            {c.error ? `Errore: ${c.error}` : `${c.last_count} eventi · sync ${c.last_sync_at ? new Date(c.last_sync_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}`}
                                        </span>
                                        <button data-testid={`ext-cal-sync-${c.id}`} onClick={() => syncNow(c)} disabled={busyCal === c.id} className="p-1.5 text-ink-400 hover:text-gold" title="Sincronizza ora">
                                            <RefreshCw size={12} className={busyCal === c.id ? "animate-spin" : ""} />
                                        </button>
                                        <button data-testid={`ext-cal-delete-${c.id}`} onClick={() => removeCal(c)} className="p-1.5 text-ink-400 hover:text-red-400" title="Scollega"><Trash2 size={12} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
